import { FC, MouseEventHandler, useCallback, useEffect, useState } from "react";
import Loader from "../../assets/components/Loader/Loader";
import { Loading, uiSlicedActions } from "../../redux/ui-slice";
import { useAppDispatch, useAppSelector } from "../../assets/hooks/hooks";
import Table from "../../assets/components/Table/Table";
import {
    Permissions,
    Project,
    ProjectsTable,
    renderSubComponentProjectTable,
} from "../../models/models";
import { ColumnDef } from "@tanstack/react-table";
import ProjectsSubRow from "./ProjectSubRow/ProjectSubRow";
import {
    useCreateProjectMutation,
    useDeleteProjectMutation,
    useGetProjectsListQuery,
    useUpdateProjectMutation,
} from "../../redux/RTKQuery/api";
import ArrowSprite from "../../assets/components/Table/Img/arrow_sprite.svg";
import TableHeader from "../../assets/components/Table/TableHeader";
import ImageSprite from "../../assets/components/ImageSprite/ImageSprite";
import PageTitle from "../../assets/components/PageTitle/PageTitle";
import {
    SearchBar,
    SearchBarDataHandler,
} from "../../assets/components/SearchBar/SearchBar";
import RemoveButton from "../../assets/components/RemoveButton/RemoveButton";
import { useNotification } from "../../assets/hooks/useNotification";
import { actionText } from "../../text";
import {
    Page,
    PaginationBar,
} from "../../assets/components/PaginationBar/PaginationBar";
import "./Projects.scss";
import { toast } from "react-toastify";

type Removed = {
    projectId: number;
};

const RemoveProjectButton: FC<Removed> = ({ projectId }) => {
    const [deleteProject] = useDeleteProjectMutation({
        fixedCacheKey: "delete_project",
    });
    const { AttentionMessageBox } = useNotification();
    const deleteProjectHandler = () =>
        AttentionMessageBox({
            textContent: actionText("delete", "project"),
        }).then((value) => {
            if (value === "yes") deleteProject(projectId);
        });

    return (
        <RemoveButton
            position="top_center"
            img="top"
            onClick={() => {
                deleteProjectHandler();
            }}
        />
    );
};

const columns: ColumnDef<Project>[] = [
    {
        header: "Activation Project Name",
        accessorKey: "name",
        cell: ({ row, getValue }) => {
            return row.original.id !== -1
                ? getValue<string>()
                : "Add Project Name";
        },
    },
    {
        header: "Number of Products",
        accessorKey: "",
        cell: ({ row }) => {
            return row.original.products?.length !== 0
                ? row.original.products?.length
                : "";
        },
    },
    {
        id: "migrated",
        header: "Migrated Products",
        cell: ({ row }) => {
            return row.original.migratedProductsCount
                ? row.original.migratedProductsCount
                : "";
        },
    },
    {
        id: "removed",
        header: () => null,
        cell: ({ row }) => {
            if (
                row.original.features &&
                row.original.features.length < 1 &&
                row.original.id !== -1
            ) {
                return <RemoveProjectButton projectId={row.original.id} />;
            }
            return null;
        },
    },
    {
        id: "expander",
        header: () => null,
        cell: ({ row }) => {
            const arrow = row.getIsExpanded() ? "arrowUp" : "arrowDown";
            return row.original.id !== -1 ? (
                <button
                    className="edit_button"
                    {...{
                        onClick: () => row.getToggleExpandedHandler(),
                        style: { cursor: "pointer" },
                    }}
                >
                    <ImageSprite iconSprite={`${ArrowSprite}#${arrow}`} />
                </button>
            ) : null;
        },
    },
];

const renderSubComponent: renderSubComponentProjectTable = ({
    row,
    subRowHandler,
}) => {
    const closeFormHandler = (id: number) => {
        row.toggleExpanded();

        if (id === -1 && subRowHandler) {
            subRowHandler(id);
        }
    };

    return (
        <ProjectsSubRow {...row.original} closeFormHandler={closeFormHandler} />
    );
};

const Projects: FC = () => {
    const dispatch = useAppDispatch();
    const pageSize: number = 30;
    const [currentPageSize, setCurrentPageSize] = useState(pageSize);
    const [pageNumber, setCurrentPage] = useState<Page>(1);

    const [isNewProjectFormVisible, setNewProjectFormVisible] = useState(false);
    const [dataTable, setData] = useState<Project[]>([]);
    const loading: Loading = useAppSelector((state) => state.ui.loading);
    const [quiresData, setQuires] = useState<SearchBarDataHandler>({});
    const [totalProjects, setTotalProjects] = useState<number>(0);

    useEffect(() => {
        setCurrentPageSize(pageSize);
    }, [pageSize]);

    const {
        data: dataProjects,
        isLoading: isLoadingProjects,
        isSuccess: isSuccessProjects,
        isError: isErrorProjects,
    } = useGetProjectsListQuery(
        { pageSize, pageNumber, name: quiresData.searchInput },
        { refetchOnFocus: true }
    );

    const [
        ,
        {
            isError: isErrorUpdateProject,
            isSuccess: isSuccessUpdateProject,
            isLoading: isLoadingUpdateProject,
            reset: resetUpdateProject,
        },
    ] = useUpdateProjectMutation({
        fixedCacheKey: "update_project",
    });

    const [
        ,
        {
            isError: isErrorCreateProject,
            isSuccess: isSuccessCreateProject,
            isLoading: isLoadingCreateProject,
            reset: resetCreateProject,
        },
    ] = useCreateProjectMutation({
        fixedCacheKey: "create_project",
    });

    const [
        ,
        {
            isError: isErrorDeleteProject,
            isSuccess: isSuccessDeleteProject,
            isLoading: isLoadingDeleteProject,
            reset: resetDeleteProject,
        },
    ] = useDeleteProjectMutation({
        fixedCacheKey: "delete_project",
    });

    const handleDeleteProject = useCallback(() => {
        if (isSuccessDeleteProject) {
            toast.success("Project deleted successfully!");
            resetDeleteProject();
        }

        if (isErrorDeleteProject) {
            toast.error("Error while deleting project!");
            resetDeleteProject();
        }
    }, [isErrorDeleteProject, isSuccessDeleteProject, resetDeleteProject]);

    const handleUpdateProject = useCallback(() => {
        if (isSuccessUpdateProject) {
            toast.success("Project updated successfully!");
            resetUpdateProject();
        }

        if (isErrorUpdateProject) {
            toast.error("Error while updating project!");
            resetUpdateProject();
        }
    }, [isErrorUpdateProject, isSuccessUpdateProject, resetUpdateProject]);

    const handleCreateProject = useCallback(() => {
        if (isSuccessCreateProject) {
            toast.success("Project created successfully!");
            resetCreateProject();
        }

        if (isErrorCreateProject) {
            toast.error("Error while creating project!");
            resetCreateProject();
        }
    }, [isErrorCreateProject, isSuccessCreateProject, resetCreateProject]);

    const handleProjectsData = useCallback(() => {
        setNewProjectFormVisible(false);
        if (isSuccessProjects && dataProjects) {
            setData(dataProjects.projects);
            if (dataProjects.filteredProjectsCount !== undefined) {
                setTotalProjects(dataProjects.filteredProjectsCount);
            } else {
                setTotalProjects(dataProjects.totalProjects);
            }
        }
        if (isErrorProjects) {
            toast.error("Error while fetching projects list.");
        }
    }, [dataProjects, isErrorProjects, isSuccessProjects]);

    const handleLoadingState = useCallback(() => {
        const loadingGlobal =
            isLoadingProjects ||
            isLoadingUpdateProject ||
            isLoadingCreateProject ||
            isLoadingDeleteProject;
        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden")
        );
    }, [
        dispatch,
        isLoadingCreateProject,
        isLoadingProjects,
        isLoadingUpdateProject,
        isLoadingDeleteProject,
    ]);

    useEffect(() => {
        handleLoadingState();
        handleCreateProject();
        handleUpdateProject();
        handleProjectsData();
        handleDeleteProject();
    }, [
        handleCreateProject,
        handleLoadingState,
        handleProjectsData,
        handleUpdateProject,
        handleDeleteProject,
    ]);

    const createNewProjectHandler: MouseEventHandler<
        HTMLButtonElement
    > = () => {
        const newRow: Project = {
            id: -1,
            name: "ProjectName",
            isOpen: true,
            features: [],
            products: [],
        };

        if (!isNewProjectFormVisible) {
            setData((prevData) => [newRow, ...prevData]);
            setNewProjectFormVisible(!isNewProjectFormVisible);
        }
    };

    const removeNewProjectHandler = (id: number) => {
        const data = dataTable.filter((product) => product.id !== id);
        setData(data);
        setNewProjectFormVisible(!isNewProjectFormVisible);
    };

    return (
        <div className="page_with_table">
            <Loader loading={loading} />
            <PageTitle text="Projects" />

            <SearchBar
                dataHandler={setQuires}
                searchInput={{
                    placeholder: "Project Name",
                }}
            />

            <div className="table_scroll_wrapper">
                <TableHeader
                    title="List of projects"
                    pageSize={pageSize}
                    onAddRowHandler={createNewProjectHandler}
                    addPermission={Permissions.PROJECT_EDIT}
                    dataTableLength={currentPageSize}
                >
                    <PaginationBar
                        pageSize={pageSize}
                        currentPage={pageNumber as number}
                        currentPageHandler={setCurrentPage}
                        totalItems={totalProjects}
                    />
                </TableHeader>

                <Table<ProjectsTable>
                    className="projects_page"
                    data={dataTable}
                    columns={columns}
                    getRowCanExpand={() => true}
                    subRowHandler={removeNewProjectHandler}
                    renderSubComponent={renderSubComponent}
                />
            </div>
        </div>
    );
};

export default Projects;
